import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
} from "@mui/material";

import { ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";

import { setInPageLoading } from "../../store/LoadingSlice";
import { RootState } from "../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../store/actions";

import { listAttendanceRecords } from "../../Service/Apis";

import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { startOfMonth } from "date-fns";
import dayjs, { Dayjs } from "dayjs";
import CustomNoRowsOverlay from "../../components/Data/NoRowsOverlayComponent";
import NothingToDisplayComponent from "../../components/Data/NothingToShowComponent";
import Footer from "../../components/Page Component/Footer";
import Navbar from "../../components/Page Component/Navbar";
import PageTitle from "../../components/Page Component/PageTitle";
import { getDataGridPaginationProps } from "utils/paginationConfig";

interface WeekDates {
  week_start: string;
  week_end: string;
}

interface AttendanceRecord {
  attendance_id: string;
  user_id: string;
  employee_name: string;
  month: number;
  year: number;
  date: string;
  present_days: number;
  paid_leaves: number;
  sick_leaves: number;
  leave_without_pay: number;
  late_marks: number;
  overtime_hours: number;
  regularized_days: number;
  created_at: string;
  week_number: number;
  employee_code: string;
  week_dates: WeekDates;
}

interface AttendanceSummary {
  total_late_marks: number;
  total_overtime: number;
  total_regularized_days: number;
  avg_late_marks: number;
  avg_overtime: number;
  total_records: number;
  date_range: {
    start_date: string;
    end_date: string;
  };
  message: string;
  user_roles: string[];
}

interface AttendanceResponse {
  results: AttendanceRecord[];
  summary: AttendanceSummary;
}

const EmployeeAttendance = () => {
  // State Hooks
  const [fromDate, setFromDate] = React.useState<Dayjs | null>(null);
  const [toDate, setToDate] = React.useState<Dayjs | null>(null);
  const userID = localStorage.getItem("id");
  const [attendanceData, setAttendanceData] =
    useState<AttendanceResponse | null>(null);
  const [forceUpdateKey, setForceUpdateKey] = useState(0);

  // Redux Hooks
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const isLoading = useSelector((state: any) => state.loading.inPageLoading);

  const rows =
    attendanceData?.results.map((record, index) => {
      return {
        ...record,
        index: index,
        sr_no: index + 1,
        month_year: dayjs(`${record.year}-${record.month}-01`).format(
          "MMM-YYYY"
        ),
        week_range: `${dayjs(record.week_dates.week_start).format(
          "DD MMM"
        )} - ${dayjs(record.week_dates.week_end).format("DD MMM, YYYY")}`,
      };
    }) || [];

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "sr_no",
        headerName: "Sr. No.",
        width: 80,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: 15 }}>
            Sr. No.
          </strong>
        ),
      },
      {
        field: "employee_name",
        headerName: "Employee Name",
        align: "left",
        width: 180,
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: 15 }}>
            Employee Name
          </strong>
        ),
      },
      {
        field: "employee_code",
        headerName: "Employee ID",
        align: "center",
        width: 110,
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: 15 }}>
            Employee ID
          </strong>
        ),
      },
      {
        field: "week_range",
        headerName: "Week Period",
        align: "center",
        width: 190,
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: 15 }}>
            Week Period
          </strong>
        ),
      },
      {
        field: "month_year",
        headerName: "Month-Year",
        align: "center",
        width: 120,
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: 15 }}>
            Month-Year
          </strong>
        ),
      },
      {
        field: "present_days",
        headerName: "Present Days",
        align: "center",
        width: 120,
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: 15 }}>
            Present Days
          </strong>
        ),
      },
      {
        field: "paid_leaves",
        headerName: "Paid Leaves",
        align: "center",
        width: 110,
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: 15 }}>
            Paid Leaves
          </strong>
        ),
      },
      {
        field: "sick_leaves",
        headerName: "Sick Leaves",
        align: "center",
        width: 110,
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: 15 }}>
            Sick Leaves
          </strong>
        ),
      },
      {
        field: "leave_without_pay",
        headerName: "Leave Without Pay",
        width: 150,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: 15 }}>
            Leave Without Pay
          </strong>
        ),
      },
      {
        field: "regularized_days",
        headerName: "Regularizations",
        width: 140,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontWeight: 700, fontSize: 15 }}>
            Regularizations
          </strong>
        ),
      },
      {
        field: "late_marks",
        headerName: "Late Marks",
        width: 120,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: 15 }}>
            Late Marks
          </strong>
        ),
      },
      {
        field: "overtime_hours",
        headerName: "Overtime Hours",
        width: 140,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-background",
        renderHeader: () => (
          <strong className="custom-header" style={{ fontSize: 15 }}>
            Overtime Hours
          </strong>
        ),
        valueFormatter: (params) => {
          return params.value ? `${params.value.toFixed(2)}h` : "0h";
        },
      },
    ];
  }, []);

  useEffect(() => {
    // API call to get the attendance records
    const fetchAttendance = async () => {
      try {
        dispatch(setInPageLoading(true));
        const response = await dispatch2(
          fetchData(
            `${
              listAttendanceRecords +
               (userID ? `?user_id=${userID}` : "") + 
              (fromDate && toDate
                ? `&date_from=${fromDate
                    .format("YYYY-MM-DD")
                    .toString()}&date_to=${toDate
                    .format("YYYY-MM-DD")
                    .toString()}`
                : "")
            }`
          )
        );
        setAttendanceData(response as AttendanceResponse);
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setInPageLoading(false));
      }
    };

    // Check whether the date range is selected before making the API call.
    if ((fromDate && toDate) || (!fromDate && !toDate)) fetchAttendance();
  }, [fromDate, toDate]);

  // Loading Spinner Component
  if (isLoading) {
    return (
      <>
        {/* Fullscreen Overlay */}
        <Box
          id="fullscreen-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backdropFilter: "blur(1.5px)", // Adjust the blur intensity as needed
            zIndex: 9999,
          }}
        />
        {/* Loader Container */}
        <Box
          id="loader-container"
          sx={{
            position: "fixed", // Use fixed positioning for the loader container
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          {/* Loader */}
          <Box
            id="loader"
            sx={{
              position: "relative", // Use relative positioning for the actual loader
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isLoading && <CircularProgress size={50} />}
          </Box>
        </Box>
      </>
    );
  }

  // Main Component
  return (
    <>
      <Box sx={{ width: "100%", mt: 12, mb: 10 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            maxWidth: "1400px",
            margin: "0 auto",
            padding: "0 16px",
          }}
        >
          <Box sx={{ width: "100%" }}>
            {/* Page Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <PageTitle>Attendance Records</PageTitle>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <DatePicker
                    label="From"
                    views={["month", "year"]}
                    sx={{
                      ".MuiInputLabel-root": {
                        transform: "translate(14px, 8px) scale(1)",
                      },
                      ".MuiInputLabel-shrink": {
                        transform: "translate(14px, -6px) scale(0.75)",
                      },
                      ".MuiInputBase-root .MuiInputBase-input": {
                        height: "0.55rem",
                        width: "8rem",
                      },
                      mx: 1,
                    }}
                    value={fromDate}
                    onChange={(newValue) =>
                      setFromDate(
                        newValue ? dayjs(startOfMonth(newValue.toDate())) : null
                      )
                    }
                    disableFuture
                  />
                  <DatePicker
                    label="To"
                    sx={{
                      ".MuiInputLabel-root": {
                        transform: "translate(14px, 8px) scale(1)",
                      },
                      ".MuiInputLabel-shrink": {
                        transform: "translate(14px, -6px) scale(0.75)",
                      },
                      ".MuiInputBase-root .MuiInputBase-input": {
                        height: "0.55rem",
                        width: "8rem",
                      },
                      mx: 1,
                    }}
                    views={["month", "year"]}
                    value={toDate}
                    onChange={(newValue) =>
                      setToDate(
                        newValue ? dayjs(startOfMonth(newValue.toDate())) : null
                      )
                    }
                    disableFuture
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disableElevation
                    onClick={() => {
                      setFromDate(null);
                      setToDate(null);
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </LocalizationProvider>
            </Box>

            {/* Summary Cards */}
            {attendanceData?.summary && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Period Card */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ height: "100%", boxShadow: 2 }}>
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        color="black"
                        gutterBottom
                      >
                        Period
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {attendanceData.summary.date_range.start_date ===
                        attendanceData.summary.date_range.end_date
                          ? dayjs(
                              attendanceData.summary.date_range.start_date
                            ).format("DD MMM, YYYY")
                          : `${dayjs(
                              attendanceData.summary.date_range.start_date
                            ).format("DD MMM")} - ${dayjs(
                              attendanceData.summary.date_range.end_date
                            ).format("DD MMM, YYYY")}`}
                      </Typography>
                      <Typography variant="body2" color="black" mt={1}>
                        {attendanceData.summary.message}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Overtime Card */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ height: "100%", boxShadow: 2 }}>
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="black"
                            gutterBottom
                          >
                            Overtime
                          </Typography>
                          <Typography variant="h5" color="primary.main">
                            {attendanceData.summary.total_overtime.toFixed(2)}h
                          </Typography>
                          <Typography
                            variant="body2"
                            color="black"
                            mt={0.5}
                          >
                            Avg:{" "}
                            {attendanceData.summary.avg_overtime.toFixed(2)}h
                            per employee
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Regularizations Card */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ height: "100%", boxShadow: 2 }}>
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="black"
                            gutterBottom
                          >
                            Regularizations
                          </Typography>
                          <Typography variant="h5" color="warning.main">
                            {attendanceData.summary.total_regularized_days}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="black"
                            mt={0.5}
                          >
                            Across {attendanceData.summary.total_records}{" "}
                            employees
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Late Marks Card */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ height: "100%", boxShadow: 2 }}>
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="black"
                            gutterBottom
                          >
                            Late Marks
                          </Typography>
                          <Typography
                            variant="h5"
                            color={
                              attendanceData.summary.total_late_marks > 0
                                ? "error.main"
                                : "success.main"
                            }
                          >
                            {attendanceData.summary.total_late_marks}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="black"
                            mt={0.5}
                          >
                            Avg:{" "}
                            {attendanceData.summary.avg_late_marks.toFixed(2)}{" "}
                            per employee
                          </Typography>
                        </Box>
                        <Box
                          sx={{ position: "relative", display: "inline-flex" }}
                        >
                          <CircularProgress
                            variant="determinate"
                            value={
                              attendanceData.summary.total_late_marks > 0
                                ? 100
                                : 0
                            }
                            size={60}
                            thickness={4}
                            sx={{
                              color:
                                attendanceData.summary.total_late_marks > 0
                                  ? "error.main"
                                  : "success.main",
                            }}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            <Box>
              {!attendanceData || attendanceData.results.length === 0 ? (
                <Box
                  sx={{
                    mt: 12,
                    left: "50%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <NothingToDisplayComponent />
                </Box>
              ) : (
                <Paper
                  elevation={2}
                  sx={{ borderRadius: 2, overflow: "hidden" }}
                >
                  <DataGrid
                    autoHeight
                    disableColumnMenu
                    sx={{
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#f5f5f5",
                      },
                      "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus":
                        {
                          outline: "none !important",
                        },
                      "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                        {
                          outline: "none !important",
                        },
                    }}
                    {...getDataGridPaginationProps()}
                    pagination
                    rows={rows}
                    columns={columns}
                    disableColumnSelector
                    disableDensitySelector
                    slots={{
                      toolbar: GridToolbar,
                      noRowsOverlay: CustomNoRowsOverlay,
                      noResultsOverlay: CustomNoRowsOverlay,
                    }}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                        style: { padding: "0.5rem 1rem" },
                      },
                    }}
                    getRowId={(row) => row.attendance_id}
                  />
                </Paper>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default EmployeeAttendance;
